import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getEventOps,
  getEventOpsCounts,
  getEventOpsPhotos,
  addManualMatch,
  suppressMatch,
  confirmMatch,
  restoreMatch,
  removePhoto,
  downloadAttendeeZip,
  rematchAttendee,
  restorePhoto,
  getEventOpsFunnel,
} from '../../Services/arndAPI';
import { faceRects } from './faceOverlay';
import Loader from '../../Components/Loader/Loader';
import ConfirmModal from '../../Components/ConfirmModal/ConfirmModal';
import PageHeader from '../../Components/PageHeader/PageHeader';
import Page from '../../Components/Page/Page';
import AudienceBuilder from './AudienceBuilder';
import CampaignTimeline from './CampaignTimeline';
import PhotoUploader from './PhotoUploader';
import { useUploadQueue } from './uploadQueue';
import ReachBadge from './ReachBadge';
import MatchChip from './MatchChip';
import { playKaching } from './kaching';
import { matchBucket, resolveViewerKey, targetMatch, faceReasons, REASON_LABEL, tileActions, decisionKind, applyDecision } from './viewerKeys';
import { ALL_PHOTOS, DELETED, toggleStatus, toggleTag, clearStatuses, activeCards, filterParams, filterTitle, rosterMatchesFilter, showSuppressedChips, keepOnAthleteChange } from './photoFilters';
import usePurchasePoll from './usePurchasePoll';
import { signedUpCount, funnelBase, athleteCount } from './funnelStages';
import { qrSummaryLine, bySourceLine, campaignLine, sharedWithShort, sharedWithNote, QR_SCANS_TOOLTIP } from './qrCard';
import { readPref, writePref, PREF_HIDE_SALES, PREF_PIPELINE_OPEN } from './opsPrefs';
import { formatEventScheduleParts, summarizePipeline } from './eventHeader';
import styles from './Styles/EventOps.module.css';

const S3_BASE = 'https://arnd-development.s3.us-west-1.amazonaws.com/';
// Some stored values are absolute URLs and some carry a leading slash
// (AttendeePhotosModal and the zip endpoint guard for the same reasons).
const s3Url = (value) =>
  !value ? '' : value.startsWith('http') ? value : S3_BASE + value.replace(/^\//, '');
// Tile outline per match bucket while an athlete is selected (SCRUM-408).
const TILE_BUCKET_CLASS = {
  auto: styles.tileAuto,
  manual: styles.tileManual,
  review: styles.tileReview,
  suppressed: styles.tileSuppressed,
};

const PAGE_SIZE = 200;
// Both the manual-refresh revenue trigger and usePurchasePoll can observe
// the same purchase — this keeps them from ringing twice for it.
const KACHING_DEBOUNCE_MS = 5000;

// Eye / eye-off (Feather outlines) for the Purchased card's hide toggle.
const EyeIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeOffIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);
// Event-day live view: silent refetch on an interval, skipped while the tab
// is hidden so a forgotten laptop doesn't poll all day (restored from
// before Task 11's feed panel — see git show b122b84).
const AUTO_REFRESH_MS = 60000;

const funnelPct = (curr, prev) =>
  prev > 0 ? `${Math.round((curr / prev) * 100)}%` : '—';

// Camera capture clock as HH:MM:SS (it is the camera's own clock, so no timezone).
const fmtClock = (iso) => (iso ? String(iso).replace('T', ' ').substring(11, 19) : '');

// The exact label parents see on the event card / blue box / cart — mirrors
// the API's expiresLabel() (event/event-time.ts, SCRUM-403) from the ops
// summary's event.remaining ({ expiresInDays, expiresInHours, isExpired }).
export const expiresLabel = (remaining) => {
  if (!remaining) return '';
  if (remaining.isExpired) return 'Photos Expired';
  if (remaining.expiresInHours > 0) {
    const h = remaining.expiresInHours;
    return `Photos Expire In ${h} Hour${h === 1 ? '' : 's'}`;
  }
  if (remaining.expiresInDays === 1) return 'Photos Expire Tomorrow';
  return `Photos Expire In ${remaining.expiresInDays} Days`;
};

const agoLabel = (iso) => {
  if (!iso) return 'never';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

export default function EventOps() {
  const { id: eventId } = useParams();

  const [summary, setSummary] = useState(null);
  const [photosPage, setPhotosPage] = useState(null);
  // SCRUM-451: statuses OR'd, sales tags AND'd, Deleted alone (photoFilters.js).
  const [filter, setFilter] = useState(ALL_PHOTOS);
  const [attendeeId, setAttendeeId] = useState(null);
  // SCRUM-423 "As parent sees it": with an athlete selected, show the set and
  // order the parent's app gallery shows — every live athlete under the same
  // parent, camera → capture time → upload time — with each tile numbered the
  // way the viewer's "n of N" counts it. A parent writing "photo 47 isn't
  // hers" becomes findable. Review work (low score, held reasons, suppressions) stays on
  // the athlete-scoped views.
  const [parentView, setParentView] = useState(false);
  const [order, setOrder] = useState('desc');
  // SCRUM-428: the grid is always ordered by routine (capture time within a
  // camera, routines placed by arrival — see api photo-runs.ts). The old
  // upload-time ordering interleaved parallel photographers and is gone.
  const sort = 'runs';
  const [camera, setCamera] = useState('');
  const [page, setPage] = useState(1);
  const [initialLoading, setInitialLoading] = useState(true);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [status, setStatus] = useState('');
  // SCRUM-430: a status line clears itself after a few seconds; only the
  // next action used to replace it, so "Photo deleted…" sat there all day.
  useEffect(() => {
    if (!status) return undefined;
    const t = setTimeout(() => setStatus(''), 6000);
    return () => clearTimeout(t);
  }, [status]);
  // The viewer tracks the photo's ID, never its array position: silent
  // refreshes replace the photos array, and a positional index would let the
  // open photo swap underneath the operator — with Delete then hitting a
  // photo they never chose.
  const [viewerPhotoId, setViewerPhotoId] = useState(null);
  const [viewerMode, setViewerMode] = useState('clean'); // 'clean' | 'parent' — remembered across photos
  const [assignTarget, setAssignTarget] = useState('');
  // SCRUM-386: a face box clicked before an athlete is chosen; the next athlete card completes the tag.
  const [pendingFaceId, setPendingFaceId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [rematching, setRematching] = useState(false);
  const [rosterSort, setRosterSort] = useState('coverage'); // coverage | alpha
  const [rosterQuery, setRosterQuery] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  // Reference selfies of a MATCHED athlete, opened from a chip in the viewer so
  // the operator can compare before confirming/suppressing (null = the selected athlete).
  const [peekAttendeeId, setPeekAttendeeId] = useState(null);
  // The peek strip renders right under the card whose 🔍 was pressed, in
  // whichever rail that card lives; an attendee is in one rail at a time.
  const togglePeek = (attendeeId) => setPeekAttendeeId((cur) => (cur === attendeeId ? null : attendeeId));
  const peekStripRef = useRef(null);
  useEffect(() => { peekStripRef.current?.scrollIntoView({ block: 'nearest' }); }, [peekAttendeeId]);
  // Name filter for the viewer's right rail (everyone not on this photo).
  const [railQuery, setRailQuery] = useState('');
  const [funnel, setFunnel] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  // The detailed pipeline strip collapses to one pill; clicking it expands
  // the strip below, remembered per browser like hideSales.
  const [pipelineOpen, setPipelineOpen] = useState(() => readPref(PREF_PIPELINE_OPEN, false));
  const togglePipelineOpen = () => {
    setPipelineOpen((prev) => {
      const next = !prev;
      writePref(PREF_PIPELINE_OPEN, next);
      return next;
    });
  };
  // SCRUM-401: per-browser switch for a screen other people can see/hear —
  // hides the Purchased card, the revenue text and the cha-ching together.
  // Read through a ref inside the purchase callbacks so those stay stable.
  const [hideSales, setHideSales] = useState(() => readPref(PREF_HIDE_SALES, false));
  const hideSalesRef = useRef(hideSales);
  hideSalesRef.current = hideSales;
  const toggleHideSales = useCallback(() => {
    const on = !hideSalesRef.current;
    setHideSales(on);
    writePref(PREF_HIDE_SALES, on);
    // The card is the only way into a purchased audience; drop it from an
    // open selection rather than leave a roster nobody can see the card for.
    if (on) setAudienceStages((prev) => {
      if (!prev.has('purchased')) return prev;
      const next = new Set(prev); next.delete('purchased'); return next;
    });
  }, []);
  // `$` flips the card from the keyboard — a quick peek without reaching for
  // the mouse. Ignored while typing (roster search, campaign bodies).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '$' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      toggleHideSales();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleHideSales]);
  // SCRUM-356: multi-select funnel stages -> AudienceBuilder.
  const [audienceStages, setAudienceStages] = useState(() => new Set());
  const [uploadOpen, setUploadOpen] = useState(false);
  // SCRUM-449: the Campaigns timeline (collapsed by default, opens after a
  // builder send) and what it last loaded, for the auto-off banner.
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const [timeline, setTimeline] = useState(null);
  // SCRUM-427: the run outlives the panel — say so in the header while it is collapsed.
  const uploadQueue = useUploadQueue(eventId);

  const selectedAthlete = summary?.roster.find((r) => r.attendeeId === attendeeId);
  const peekAthlete = peekAttendeeId ? summary?.roster.find((r) => r.attendeeId === peekAttendeeId) : null;
  const peekStrip = peekAthlete ? (
    <div className={styles.peekStrip} ref={peekStripRef}>
      <div className={styles.peekHeader}>
        <strong>{peekAthlete.fullName.trim()}</strong>
        <button type="button" className={styles.peekClose} onClick={() => setPeekAttendeeId(null)} aria-label="Close reference selfies">×</button>
      </div>
      <div className={styles.peekShots}>
        {(peekAthlete.selfieUrls?.length ? peekAthlete.selfieUrls : [peekAthlete.selfieUrl]).filter(Boolean).map((u, i) => (
          <a key={i} href={s3Url(u)} target="_blank" rel="noreferrer" title="Reference selfie — open full resolution">
            <img src={s3Url(u)} alt={`${peekAthlete.fullName} reference ${i + 1}`} className={styles.peekShot} />
          </a>
        ))}
        {!(peekAthlete.selfieUrls?.length || peekAthlete.selfieUrl) && <span className={styles.subtle}>No reference photos on file</span>}
      </div>
    </div>
  ) : null;
  const closeProfile = () => setProfileOpen(false);
  // SCRUM-430: Escape closes the athlete profile panel like it closes the viewer.
  useEffect(() => {
    if (!profileOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || confirmDelete) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      e.preventDefault();
      setProfileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [profileOpen, confirmDelete]);

  // API order is least-covered-first; alpha is a client-side re-sort.
  const sortedRoster = summary
    ? rosterSort === 'alpha'
      ? summary.roster.slice().sort((a, b) => a.fullName.trim().localeCompare(b.fullName.trim()))
      : summary.roster
    : [];

  // Search matches the athlete's name OR the parent's name/email, so the
  // operator can find a kid from a parent's message or a purchase.
  const rosterMatchesQuery = (r) => {
    const q = rosterQuery.trim().toLowerCase();
    if (!q) return true;
    return [r.fullName, r.parentName, r.parentEmail]
      .some((v) => (v || '').toLowerCase().includes(q));
  };
  // The count-card filter narrows the roster too (the selected athlete always
  // stays visible so the active selection can't vanish; Deleted keeps everyone).
  const visibleRoster = sortedRoster.filter((r) => rosterMatchesFilter(r, filter, attendeeId) && rosterMatchesQuery(r));

  // Stale-response guard: only the latest request may write state. Rapid
  // view/athlete clicks otherwise race, and the slowest response wins.
  // A response also has to be for the CURRENT athlete selection (or none):
  // a slow summary for athlete A must never land after the operator moved to
  // B. The ref tracks the selection without waiting for a callback rebuild.
  const attendeeIdRef = useRef(attendeeId);
  attendeeIdRef.current = attendeeId;
  // Last event-wide counts (from a full summary or a counts call made with no
  // athlete): shown instantly when the athlete is cleared, then refreshed.
  const eventWideCounts = useRef(null);
  const summarySeq = useRef(0);
  const loadSummary = useCallback(async () => {
    const seq = ++summarySeq.current;
    const requested = attendeeId;
    const data = await getEventOps(eventId, requested);
    if (seq !== summarySeq.current) return null;
    // A superseded seq is genuinely stale (a newer request is already in
    // flight or landed) — drop it entirely. But a response whose seq is
    // still current, for a selection the operator has since moved off of,
    // still carries fresh roster/pipeline data worth keeping; only its
    // counts (scoped to the no-longer-current athlete) get dropped, and it
    // must not seed the event-wide counts cache.
    if ((requested ?? null) !== (attendeeIdRef.current ?? null)) {
      setSummary((s) => ({ ...data, counts: s?.counts ?? data.counts }));
      return null;
    }
    if (!requested) eventWideCounts.current = data.counts;
    setSummary(data);
    return data;
  }, [eventId, attendeeId]);

  // SCRUM-451: athlete clicks refetch only the counts (~the photos request's
  // cost), not the whole summary; roster/pipeline stay as they are.
  const countsSeq = useRef(0);
  const loadCounts = useCallback(async () => {
    const seq = ++countsSeq.current;
    const requested = attendeeId;
    const counts = await getEventOpsCounts(eventId, requested);
    if (seq !== countsSeq.current) return null;
    if (!requested) eventWideCounts.current = counts;
    if ((requested ?? null) !== (attendeeIdRef.current ?? null)) return null;
    setSummary((s) => (s ? { ...s, counts } : s));
    return counts;
  }, [eventId, attendeeId]);

  // Funnel failures must never take down the ops console — the panel just
  // shows its last-good data (or stays hidden on first load).
  const loadFunnel = useCallback(async () => {
    try {
      setFunnel(await getEventOpsFunnel(eventId));
    } catch {
      /* keep previous funnel */
    }
  }, [eventId]);

  // Stale-response guard: only the latest request may write state. Rapid
  // view/athlete clicks otherwise race, and the slowest response wins.
  const photosSeq = useRef(0);
  const asParent = parentView && !!attendeeId;
  const loadPhotos = useCallback(async () => {
    const seq = ++photosSeq.current;
    const data = await getEventOpsPhotos(eventId, {
      ...filterParams(filter, { asParent }),
      attendeeId: attendeeId ?? undefined,
      page,
      pageSize: PAGE_SIZE,
      order,
      sort: asParent ? 'gallery' : sort,
      scope: asParent ? 'parent' : undefined,
      camera: camera || undefined,
    });
    if (seq !== photosSeq.current) return null;
    setPhotosPage(data);
    return data;
  }, [eventId, filter, attendeeId, page, order, sort, camera, asParent]);

  // Cha-ching (SCRUM-419): the purchase feed is the ONLY thing that rings.
  // It used to share the job with a funnel-revenue diff on refresh, and the
  // two timers (30 s poll, 60 s auto-refresh) saw the same sale up to a
  // minute apart — well outside the 5 s debounce, so one purchase rang
  // twice. The debounce now only collapses the rows of one poll batch into
  // a single ring.
  const lastKachingAtRef = useRef(0);
  // Hidden sales: no sound, and the status line only confirms a sale
  // happened — never the amount or the parent. The debounce still advances
  // so un-hiding does not ring for a purchase that already went by.
  const ringKaching = useCallback((message) => {
    const now = Date.now();
    if (now - lastKachingAtRef.current < KACHING_DEBOUNCE_MS) return;
    lastKachingAtRef.current = now;
    if (hideSalesRef.current) { setStatus('💰 New purchase'); return; }
    playKaching();
    setStatus(message);
  }, []);

  // usePurchasePoll is a live-view trigger independent of any UI panel: a
  // new purchase row it turns up (never the initial load) rings the
  // register. refreshAll kicks it too, so Refresh / a send still ring
  // promptly instead of waiting for the next 30 s tick.
  const handlePurchase = useCallback((row) => {
    ringKaching(`💰 ${row.parent} — ${row.detail}`);
  }, [ringKaching]);
  const pollPurchases = usePurchasePoll(eventId, handlePurchase);

  const refreshAll = useCallback(async ({ silent } = {}) => {
    try {
      await Promise.all([loadSummary(), loadPhotos(), loadFunnel(), pollPurchases()]);
      // Never clear status here: mutation handlers set their confirmation
      // right before a silent refresh, and the auto-refresh tick must not
      // erase errors the operator hasn't read.
    } catch (e) {
      setStatus(e.message || 'Failed to load');
    } finally {
      if (!silent) setInitialLoading(false);
    }
  }, [loadSummary, loadPhotos, loadFunnel, pollPurchases]);

  // Initial load only — navigation refetches are handled below and do not
  // re-run the roster aggregation.
  useEffect(() => {
    eventWideCounts.current = null;
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Event-day live view: silent refetch on an interval, skipped while the
  // tab is hidden so a forgotten laptop doesn't poll all day.
  const refreshRef = useRef(refreshAll);
  refreshRef.current = refreshAll;
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      refreshRef.current({ silent: true });
    }, AUTO_REFRESH_MS);
    return () => clearInterval(t);
  }, [autoRefresh]);

  // Counts re-scope when the athlete selection changes — counts only; on
  // unselect the cached event-wide counts show at once, then refresh.
  const firstAthleteEffect = useRef(true);
  useEffect(() => {
    if (firstAthleteEffect.current) { firstAthleteEffect.current = false; return; }
    const cached = eventWideCounts.current;
    if (!attendeeId && cached) setSummary((s) => (s ? { ...s, counts: cached } : s));
    // A stale counts error (the operator has since selected someone else)
    // is not worth surfacing — the current selection's own request will
    // report its own failure, if any.
    const requested = attendeeId;
    loadCounts().catch((e) => {
      if ((requested ?? null) !== (attendeeIdRef.current ?? null)) return;
      setStatus(e.message);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendeeId]);

  // Photos-only refetch on view/page/sort changes; grid stays mounted.
  const firstPhotosEffect = useRef(true);
  useEffect(() => {
    if (firstPhotosEffect.current) { firstPhotosEffect.current = false; return; }
    let cancelled = false;
    setPhotosLoading(true);
    loadPhotos()
      .catch((e) => { if (!cancelled) setStatus(e.message); })
      .finally(() => { if (!cancelled) setPhotosLoading(false); });
    return () => { cancelled = true; };
  }, [loadPhotos]);

  // Clamp the page when deletions/matching shrink the result set below it.
  useEffect(() => {
    if (!photosPage) return;
    const last = Math.max(1, Math.ceil(photosPage.total / PAGE_SIZE));
    if (page > last) setPage(last);
  }, [photosPage, page]);

  // An athlete picked for one photo must never apply to another.
  useEffect(() => { setAssignTarget(''); setPendingFaceId(null); setPeekAttendeeId(null); }, [viewerPhotoId]);

  const toggleAudienceStage = (stage) => {
    setAudienceStages((prev) => {
      const next = new Set(prev);
      if (next.has(stage)) next.delete(stage); else next.add(stage);
      return next;
    });
  };

  const photos = useMemo(() => photosPage?.photos ?? [], [photosPage]);
  const total = photosPage?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const viewerIndex = viewerPhotoId ? photos.findIndex((p) => p.id === viewerPhotoId) : -1;
  // SCRUM-426: a decision never moves the viewer. When x/c drops the open
  // photo out of this view, `heldPhoto` (the photo with the decided chip
  // patched) keeps it open so the other profiles on it can be worked too —
  // Elisha's duplicates all sit on the same photos. Cleared on navigation.
  const [heldPhoto, setHeldPhoto] = useState(null);
  const held = viewerIndex < 0 && !!heldPhoto && heldPhoto.id === viewerPhotoId;
  const viewerPhoto = viewerIndex >= 0 ? photos[viewerIndex] : held ? heldPhoto : null;
  useEffect(() => {
    setHeldPhoto((h) => (h && h.id !== viewerPhotoId ? null : h));
  }, [viewerPhotoId]);
  // SCRUM-338: suppressed matches stay out of the way unless the operator has
  // Review or Suppressed selected, where Restore lives.
  // Suppressed chips stay hidden otherwise, but with Review/Suppressed
  // selected — and in an athlete's superset (SCRUM-408), where a suppressed
  // photo is a legitimate member — they must show so c can confirm them.
  const showSuppressed = showSuppressedChips(filter, attendeeId);
  const viewerMatches = viewerPhoto
    ? viewerPhoto.matchedAttendees.filter((att) => showSuppressed || att.status !== 'suppressed')
    : [];

  // While a held photo is open its old slot is taken by the photo after it,
  // so "next" is the photo now at that index and "prev" the one before.
  const lastViewerIndexRef = useRef(-1);
  const navIndex = viewerIndex >= 0 ? viewerIndex : Math.min(Math.max(lastViewerIndexRef.current, 0), photos.length);
  const prevPhoto = navIndex > 0 ? photos[navIndex - 1] : null;
  const nextPhoto = held ? photos[navIndex] ?? null : photos[navIndex + 1] ?? null;

  // Keyboard shortcuts while the viewer is open (SCRUM-408): x suppress,
  // c confirm, d delete (confirm dialog), ← → move, Esc closes. The rules
  // live in viewerKeys.js; the target is the selected athlete's match on
  // this photo. No auto-advance — Imad reviews at his own pace (SCRUM-426:
  // not even when the decision takes the photo out of the view).
  const viewerTarget = viewerPhoto ? targetMatch({ attendeeId, matches: viewerPhoto.matchedAttendees }) : null;
  useEffect(() => {
    if (!viewerPhoto) return undefined;
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (confirmDelete) return; // the dialog owns the keyboard
      const action = resolveViewerKey(e.key, { deleted: filter.deleted, attendeeId, matches: viewerPhoto.matchedAttendees });
      if (!action) return;
      e.preventDefault();
      if (action.type === 'prev') { if (prevPhoto) setViewerPhotoId(prevPhoto.id); }
      else if (action.type === 'next') { if (nextPhoto) setViewerPhotoId(nextPhoto.id); }
      else if (action.type === 'close') setViewerPhotoId(null);
      else if (action.type === 'delete') setConfirmDelete(viewerPhoto);
      else if (action.type === 'suppress') handleMatchAction(viewerPhoto, action.match, suppressMatch, action.verb);
      else if (action.type === 'confirm') handleMatchAction(viewerPhoto, action.match, confirmMatch, action.verb);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerPhoto, prevPhoto, nextPhoto, filter, attendeeId, confirmDelete]);

  // If something other than a decision removed the open photo from this
  // view (a filter change, another operator), move to the photo now at its
  // position; close only when the view has nothing left (SCRUM-408). A
  // decision holds the photo instead (SCRUM-426, `heldPhoto`).
  useEffect(() => {
    if (viewerIndex >= 0) lastViewerIndexRef.current = viewerIndex;
  }, [viewerIndex]);
  useEffect(() => {
    if (!viewerPhotoId || !photosPage || viewerIndex >= 0 || held) return;
    const next = photos[Math.min(Math.max(lastViewerIndexRef.current, 0), photos.length - 1)];
    setViewerPhotoId(next ? next.id : null);
  }, [viewerPhotoId, photosPage, viewerIndex, photos, held]);

  const gridAnchorRef = useRef(null);
  const rosterCardRefs = useRef({});

  const applyFilter = (next, nextAttendeeId = attendeeId) => {
    setProfileOpen(false);
    setFilter(next);
    setAttendeeId(next.deleted ? null : nextAttendeeId);
    setParentView(false);
    setPage(1);
    setViewerPhotoId(null);
  };

  // SCRUM-356 Task 15 follow-up: clicking an athlete chip or a parent's name
  // in the AudienceBuilder table jumps to that athlete the same way the
  // roster card click does, then scrolls the photo grid into view. The
  // builder stays open and the selected funnel stages are untouched — this
  // never touches audienceStages.
  const handleSelectAthleteFromAudience = (newAttendeeId) => {
    applyFilter(keepOnAthleteChange(filter), newAttendeeId);
    // Bring the athlete's card into view in the horizontal roster strip first
    // (it may be scrolled off to the side), then the photo grid below it. The
    // card ref exists once the roster has rendered with the new selection.
    requestAnimationFrame(() => {
      rosterCardRefs.current[newAttendeeId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      gridAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // SCRUM-386: `faceId` is set when the operator clicked a face box in the
  // Clean viewer; the match then carries that face (tile + head inset for
  // the parent). Without it, the Assign button makes a face-less manual match.
  const handleAssign = async (photo, faceId = null, attendeeId = assignTarget) => {
    if (!attendeeId) return;
    const who = summary.roster.find((r) => r.attendeeId === attendeeId);
    try {
      await addManualMatch(photo.id, attendeeId, faceId);
      setAssignTarget('');
      setPendingFaceId(null);
      await refreshAll({ silent: true });
      setStatus(
        `Assigned to ${who?.fullName || 'athlete'}${faceId ? ' on the clicked face' : ''} — visible in their app gallery now`,
      );
    } catch (e) {
      setStatus(e.message);
    }
  };

  // SCRUM-338: one handler for the chip actions — fn is suppressMatch,
  // confirmMatch or restoreMatch.
  // SCRUM-466: optimistic. The decision is applied to the page (and the
  // held viewer copy) BEFORE the request, so the tile and chips change
  // instantly — from an event venue every request costs ~1.3 s. On success
  // the grid and counts re-read in the background; on failure the photo is
  // put back and the error shown.
  // SCRUM-436: the grid tiles use it too, with `hold: false` — no viewer is
  // open, so a tile the decision drops from the view leaves the grid on the
  // background re-read.
  const handleMatchAction = async (photo, att, fn, verb, { hold = true } = {}) => {
    const action = fn === suppressMatch ? 'suppress' : fn === confirmMatch ? 'confirm' : fn === restoreMatch ? 'restore' : null;
    const kind = decisionKind(action, att);
    const threshold = summary?.event?.matchThreshold ?? 95;
    const replacePhoto = (next) =>
      setPhotosPage((pg) =>
        pg ? { ...pg, photos: pg.photos.map((x) => (x.id === photo.id ? next : x)) } : pg,
      );
    if (kind) {
      const patched = applyDecision(photo, att.attendeeId, kind, threshold);
      replacePhoto(patched);
      // Hold the photo BEFORE a re-read can drop it from the view (SCRUM-426).
      if (hold) setHeldPhoto(patched);
    }
    setStatus(`${verb} ${att.fullName?.trim()} on this photo…`);
    try {
      await fn(photo.id, att.attendeeId);
      setStatus(`${verb} ${att.fullName?.trim()} on this photo`);
      // Background: the grid (so view membership catches up) and the counts.
      // refreshAll re-reads the grid too and reports its own failures.
      refreshAll({ silent: true });
    } catch (e) {
      // Put the photo back exactly as it was, and say why.
      replacePhoto(photo);
      if (hold) setHeldPhoto((h) => (h && h.id === photo.id ? photo : h));
      setStatus(`Not saved — ${e.message}`);
    }
  };

  // SCRUM-436: one grid-tile action at a time per photo; the buttons are
  // disabled until the refresh after it lands, so a double click can't
  // send the same decision twice.
  const [tileBusyId, setTileBusyId] = useState(null);
  const TILE_ACTION_FN = { confirm: confirmMatch, suppress: suppressMatch, restore: restoreMatch };
  const handleTileAction = async (photo, match, action) => {
    setTileBusyId(photo.id);
    try {
      await handleMatchAction(photo, match, TILE_ACTION_FN[action.type], action.verb, { hold: false });
    } finally {
      // Only clear our own id: a second tile actioned meanwhile stays busy.
      setTileBusyId((id) => (id === photo.id ? null : id));
    }
  };

  const handleDelete = async () => {
    const photo = confirmDelete;
    setConfirmDelete(null);
    try {
      await removePhoto(photo.id);
      // SCRUM-430: a delete from the viewer moves on to the next photo (the
      // previous one at the end of the page) instead of dropping back to
      // the grid — d, Enter, d, Enter walks a run of bad frames.
      if (viewerPhotoId === photo.id) setViewerPhotoId(nextPhoto?.id ?? prevPhoto?.id ?? null);
      await refreshAll({ silent: true });
      setStatus('Photo deleted — removed from attendee galleries (see Deleted to restore)');
    } catch (e) {
      setStatus(e.message);
    }
  };

  const handleRestore = async (photo) => {
    try {
      await restorePhoto(photo.id);
      setViewerPhotoId(null);
      await refreshAll({ silent: true });
      setStatus('Photo restored — back in attendee galleries');
    } catch (e) {
      setStatus(e.message);
    }
  };

  const handleRematch = async () => {
    if (!selectedAthlete || rematching) return;
    setRematching(true);
    setStatus(`Re-running face matching for ${selectedAthlete.fullName}…`);
    try {
      const r = await rematchAttendee(eventId, selectedAthlete.attendeeId);
      await refreshAll({ silent: true });
      setStatus(
        r.added > 0
          ? `${selectedAthlete.fullName}: ${r.added} new match${r.added === 1 ? '' : 'es'} found (${r.after} total)`
          : `${selectedAthlete.fullName}: no new matches (${r.after} total) — if that's wrong, check their selfie`
      );
    } catch (e) {
      setStatus(e.message);
    } finally {
      setRematching(false);
    }
  };

  const handleDownloadZip = async () => {
    if (!selectedAthlete) return;
    setDownloading(true);
    setStatus(`Preparing zip for ${selectedAthlete.fullName}… (large downloads take a while)`);
    try {
      await downloadAttendeeZip(
        eventId,
        selectedAthlete.attendeeId,
        `${selectedAthlete.fullName.trim().replace(/\s+/g, '-')}-photos.zip`
      );
      setStatus(`Zip for ${selectedAthlete.fullName} saved to your downloads`);
    } catch (e) {
      setStatus(e.message);
    } finally {
      setDownloading(false);
    }
  };

  if (initialLoading && !summary) return <Loader />;
  if (!summary) return <div className={styles.status}>{status || 'No event found'}</div>;

  return (
    <Page wide>
      {timeline?.event?.showOffBanner && (
        <div className={styles.autoOffBanner} role="status">
          Automatic campaigns are OFF for this event — no reminders will go out.{' '}
          <Link to={`/events/${eventId}`}>Turn them on in the event form</Link>
        </div>
      )}
      <PageHeader
        title={summary.event.name}
        subtitle={
          <>
          {summary.event?.startsAt ? (
            <div className={styles.eventWhen}>
              <span className={styles.subtle}>
                {formatEventScheduleParts(summary.event).map((part, i) =>
                  part.type === 'time' ? (
                    <span key={i} title={part.title}>{part.text}</span>
                  ) : (
                    <React.Fragment key={i}>{part.text}</React.Fragment>
                  )
                )}
                {expiresLabel(summary.event?.remaining) && (
                  <> · <strong className={summary.event.remaining.isExpired ? styles.expiredLabel : ''} title="Exactly what parents see in the app">{expiresLabel(summary.event.remaining)}</strong></>
                )}
              </span>
              {summary.event?.location && (
                <span className={styles.subtle}>{summary.event.location}</span>
              )}
            </div>
          ) : (
            <span className={styles.subtle}>{summary.event.date?.substring(0, 10)} · {summary.event.location}</span>
          )}
          {summary.pipeline && (() => {
            // SCRUM-429: the status strip, collapsed to one pill (follow-up):
            // every stage still shows its live number and the one signal
            // that goes red when it stalls; all event-wide (counts.photos is
            // athlete-scoped when one is selected). The pill names only the
            // worst stage — click it for the full strip.
            const p = summary.pipeline;
            const h = p.health;
            const total = p.total ?? (attendeeId ? null : summary.counts.photos);
            const ratio = (n) => (total == null ? n : `${n}/${total}`);
            const secs = (n) => (n == null ? '—' : n < 90 ? `${n} s` : `${Math.round(n / 60)} min`);
            const minutesSince = (iso) => (iso ? (Date.now() - new Date(iso).getTime()) / 60000 : Infinity);
            const cls = (state) => (state === 'bad' ? styles.pipeBad : state === 'warn' ? styles.pipeLag : state === 'idle' ? styles.pipeIdle : styles.pipeOk);
            const landing = p.lastHour > 0 ? 'ok' : 'idle';
            const indexing = h && h.oldestWaitingS != null && h.oldestWaitingS > 120 ? 'bad' : h && h.waiting > 0 ? 'warn' : 'ok';
            const rendering = h && h.unrendered > 60 ? 'bad' : h && h.unrendered > 0 ? 'warn' : total != null && p.thumbnailed < total ? 'warn' : 'ok';
            const matching = h && h.owed > 0 && minutesSince(h.lastSweepAt) > 6 ? 'bad' : h && h.owed > 0 ? 'warn' : 'ok';
            const delivery = h && h.visibleNoPreview > 0 ? 'bad' : 'ok';
            const pill = summarizePipeline([
              { name: 'Landing', state: landing, detail: h ? `${h.landed10m} in 10 min` : `${p.lastHour} in last hour` },
              { name: 'Indexing', state: indexing, detail: h && h.waiting > 0 ? `${h.waiting} waiting (oldest ${secs(h.oldestWaitingS)})` : ratio(p.indexed) },
              { name: 'Rendering', state: rendering, detail: h && h.unrendered > 0 ? `${h.unrendered} pending` : ratio(p.thumbnailed) },
              { name: 'Matching', state: matching, detail: h ? `${h.owed} owed` : '' },
              { name: 'Delivery', state: delivery, detail: h ? `${h.visibleNoPreview} without preview` : '' },
            ]);
            return (
              <>
                <button
                  type="button"
                  className={`${styles.pipelinePill} ${cls(pill.level)}`}
                  onClick={togglePipelineOpen}
                  aria-expanded={pipelineOpen}
                  title="Click for the full pipeline breakdown"
                >
                  ● {pill.text}
                </button>
                {pipelineOpen && (
                  <div className={styles.pipeline}>
                    <span className={cls(landing)} title="Photos that reached the FTP box → S3 → a Photos row">
                      <b>Landing</b> {h ? `${h.landed10m} in 10 min` : `${p.lastHour} in last hour`} · last {agoLabel(p.lastPhotoAt)}
                    </span>
                    <span className={cls(indexing)} title="Rekognition IndexFaces on the SQS worker. Red when the oldest un-indexed photo is older than 2 minutes — the worker is stuck">
                      <b>Indexing</b> {ratio(p.indexed)}
                      {h && h.waiting > 0 && ` · ${h.waiting} waiting (oldest ${secs(h.oldestWaitingS)})`}
                      {h && h.lagP50S != null && ` · lag ${secs(h.lagP50S)} / max ${secs(h.lagMaxS)}`}
                    </span>
                    <span className={cls(rendering)} title="Thumbnail, clean and face tiles rendered by the render loop ~30 s after indexing">
                      <b>Thumbs</b> {ratio(p.thumbnailed)}{h && h.unrendered > 0 && ` · ${h.unrendered} pending`}
                    </span>
                    {h && (
                      <span className={cls(matching)} title="The match clock sweeps an event when an RSVP'd athlete has not been searched since the newest photo was indexed. Red when athletes are owed a sweep and none ran in 6 minutes">
                        <b>Matching</b> last sweep {agoLabel(h.lastSweepAt)} · {h.owed} owed · {h.zeroMatch} with no photos
                      </span>
                    )}
                    {h && (
                      <span className={cls(delivery)} title="A visible match must have its preview before the parent is notified; a lasting count here means the render queue or backstop is failing">
                        <b>Delivery</b> {h.visibleNoPreview} without preview · {h.pushes10m} pushes in 10 min
                      </span>
                    )}
                    {p.rekognition && (
                      <span title={`Rekognition since the day before the event, ${p.rekognition.region} at $${p.rekognition.usdPerImage}/image: ${p.rekognition.calls.IndexFaces.toLocaleString()} IndexFaces, ${p.rekognition.calls.SearchFacesByImage.toLocaleString()} SearchFacesByImage, ${p.rekognition.calls.DetectFaces.toLocaleString()} DetectFaces`}>
                        Rekognition ${p.rekognition.usd.toFixed(2)}
                        {' '}· {p.rekognition.calls.IndexFaces.toLocaleString()} indexed
                        {' '}· {p.rekognition.calls.SearchFacesByImage.toLocaleString()} searches
                      </span>
                    )}
                  </div>
                )}
              </>
            );
          })()}
          </>
        }
        actions={
          <>
            <label className={styles.autoRefresh}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh
            </label>
            <button className={styles.refreshBtn} onClick={() => refreshAll({ silent: true })}>Refresh</button>
          </>
        }
      />

      {funnel && (() => {
        const f = funnel.funnel;
        // Leads are per-event (SCRUM-449 follow-up): parents who signed up
        // (account or athlete profile) during this event's window and
        // haven't joined it yet. "Signed up" is leads plus everyone who got
        // at least as far as joining. SCRUM-487: Signed up → Purchased are
        // counted in athletes (the athletes of the families at each stage,
        // f.athletes), parents on the line below; percentages are shares of
        // the meet's athletes when tracked (eventAthletes.json), otherwise
        // of RSVP'd athletes. QR scans and Installs count scans and devices,
        // so they carry no percentage (funnelStages.js).
        const leads = funnel.leads ?? f.leads;
        const signedUp = signedUpCount(leads, f.joinedParents);
        const A = f.athletes;
        const meet = athleteCount(eventId);
        const { base, baseLabel, aboveRsvp } = funnelBase(f, meet);
        const pctLine = (n) => <span className={styles.funnelPct}>{funnelPct(n, base)} {baseLabel}</span>;
        const parentsLine = (n) => <span className={styles.funnelSplit}>{n} parent{n === 1 ? '' : 's'}</span>;
        const stuckLine = (n) => (
          <span className={styles.funnelStuck} title="Parents whose furthest stage is this one — what clicking the card selects">{n} parent{n === 1 ? '' : 's'} stuck here</span>
        );
        const split = (x) => (x ? `${x.ios} iOS · ${x.android} Android` : '');
        // SCRUM-438: an event with its own link counts that link's scans;
        // bare /app hits during the event's window are credited too (see
        // qrCard.js — generic can be null when the WordPress endpoint is
        // unreachable, which must render as nothing extra, not an error).
        const q = f.qrScans;
        const stageSplit = (stage) => split(f.platform?.[stage]);
        return (
        <div className={styles.funnel}>
          {/* SCRUM-463: athletes competing at the meet — the funnel's real
              universe. Display only, and only for events tracked by hand in
              eventAthletes.json until the event page takes the number. */}
          {meet && (
            <div
              className={styles.funnelCard}
              title={`Athletes competing at the meet, from its session rosters:\n${meet.sessions.map((x) => `${x.label}: ${x.athletes}`).join('\n')}\n\nSigned up through Purchased count athletes: every athlete of each family at that stage (a family counts at least one), with parents on the line below. Their percentages are shares of this number. QR scans and Installs count scans and devices, so they have no percentage.`}
            >
              <span className={styles.funnelNum}>{meet.athletes}</span>
              <span className={styles.funnelLabel}>Athletes</span>
              <span className={styles.funnelSplit}>{meet.sessions.length} sessions</span>
            </div>
          )}
          {/* SCRUM-449 follow-up: back as the funnel's first card, display
              only — no audience, not clickable. The API restores the old
              installs fields (f.installs / f.stuck.installed) alongside the
              rest of the pipeline; this card just renders them again. */}
          <div className={styles.funnelCard} title={QR_SCANS_TOOLTIP}>
            <span className={styles.funnelNum}>{q ? q.total : '—'}</span>
            <span className={styles.funnelLabel}>QR scans</span>
            {!q && <span className={styles.funnelPct}>unavailable</span>}
            {/* Short lines (the old long-form ones crowded a narrow card):
                event link · generic link, the iOS/Android split like the
                other cards, then sources, then campaign clicks. Overlapping events are named in the tooltip. */}
            {qrSummaryLine(q) && <span className={styles.funnelSplit}>{qrSummaryLine(q)}</span>}
            {q && q.ios != null && <span className={styles.funnelSplit}>{split(q)}</span>}
            {q && bySourceLine(q.bySource) && <span className={styles.funnelSplit}>{bySourceLine(q.bySource)}</span>}
            {q && campaignLine(q.campaign) && <span className={styles.funnelSplit}>{campaignLine(q.campaign)}</span>}
            {sharedWithShort(q?.generic) && (
              <span className={styles.funnelWarn} title={sharedWithNote(q.generic)}>{sharedWithShort(q.generic)}</span>
            )}
          </div>
          <div
            className={styles.funnelCard}
            title="App installs on devices without an account, between registration opening and sales close. Staff devices excluded."
          >
            <span className={styles.funnelNum}>{f.installs ? f.installs.total : '—'}</span>
            <span className={styles.funnelLabel}>Installs</span>
            {!f.installs && <span className={styles.funnelPct}>unavailable</span>}
            {f.installs && <span className={styles.funnelSplit}>{split(f.installs)}</span>}
            {f.installs && f.installs.excluded > 0 && (
              <span className={styles.funnelSplit} title="Our own phones (admin accounts) and OneSignal test subscriptions, left out of the count">
                {f.installs.excluded} staff device{f.installs.excluded === 1 ? '' : 's'} left out
              </span>
            )}
          </div>
          <button
            type="button"
            className={`${styles.funnelCard} ${audienceStages.has('lead') ? styles.funnelCardActive : ''}`}
            data-stage="lead"
            onClick={() => toggleAudienceStage('lead')}
            title="Account or athlete profile created during this event, not joined yet. Staff excluded."
          >
            <span className={styles.funnelNum}>{A.signedUp}</span>
            <span className={styles.funnelLabel}>Signed up</span>
            {aboveRsvp && pctLine(A.signedUp)}
            {signedUp != null && parentsLine(signedUp)}
            {leads && stuckLine(leads.count)}
          </button>
          <button
            type="button"
            className={`${styles.funnelCard} ${audienceStages.has('rsvp') ? styles.funnelCardActive : ''}`}
            data-stage="rsvp"
            onClick={() => toggleAudienceStage('rsvp')}
          >
            <span className={styles.funnelNum}>{A.rsvp}</span>
            <span className={styles.funnelLabel}>RSVPs</span>
            {aboveRsvp && pctLine(A.rsvp)}
            {parentsLine(f.joinedParents)}
            <span className={styles.funnelSplit}>{stageSplit('joinedParents')}</span>
            {f.stuck && stuckLine(f.stuck.rsvp)}
          </button>
          <button
            type="button"
            className={`${styles.funnelCard} ${audienceStages.has('matched') ? styles.funnelCardActive : ''}`}
            data-stage="matched"
            onClick={() => toggleAudienceStage('matched')}
          >
            <span className={`${styles.funnelNum} ${styles.numMatched}`}>{A.matched}</span>
            <span className={styles.funnelLabel}>Matched</span>
            {pctLine(A.matched)}
            {parentsLine(f.matchedParents)}
            <span className={styles.funnelSplit}>{stageSplit('matchedParents')}</span>
            {f.stuck && stuckLine(f.stuck.matched)}
          </button>
          <button
            type="button"
            className={`${styles.funnelCard} ${audienceStages.has('cart') ? styles.funnelCardActive : ''}`}
            data-stage="cart"
            onClick={() => toggleAudienceStage('cart')}
          >
            <span className={`${styles.funnelNum} ${styles.numInCart}`}>{A.cart}</span>
            <span className={styles.funnelLabel}>Added to cart</span>
            {pctLine(A.cart)}
            {parentsLine(f.cartParents)}
            <span className={styles.funnelSplit}>{stageSplit('cartParents')}</span>
            {f.stuck && stuckLine(f.stuck.cart)}
          </button>
          {hideSales ? (
          // SCRUM-432: hidden only blanks the numbers. The card still selects
          // the Purchased audience for a campaign; the eye is the only thing
          // that unhides, so a screen-share stays clean while ops keeps working.
          <button
            type="button"
            className={`${styles.funnelCard} ${styles.funnelCardHidden} ${audienceStages.has('purchased') ? styles.funnelCardActive : ''}`}
            data-stage="purchased"
            onClick={() => toggleAudienceStage('purchased')}
            title="Sales hidden — the card still selects the Purchased audience; the eye shows the numbers"
          >
            <span
              role="button"
              tabIndex={0}
              className={styles.funnelHide}
              title="Show sales and unmute the cha-ching on this screen ($)"
              aria-label="Show sales"
              onClick={(e) => { e.stopPropagation(); toggleHideSales(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggleHideSales(); } }}
            >
              <EyeIcon />
            </span>
            <span className={styles.funnelNum}>•••</span>
            <span className={styles.funnelLabel}>Purchased</span>
            <span className={styles.funnelPct}>hidden</span>
          </button>
          ) : (
          <button
            type="button"
            className={`${styles.funnelCard} ${audienceStages.has('purchased') ? styles.funnelCardActive : ''}`}
            data-stage="purchased"
            onClick={() => toggleAudienceStage('purchased')}
          >
            <span
              role="button"
              tabIndex={0}
              className={styles.funnelHide}
              title="Hide sales and mute the cha-ching on this screen ($)"
              aria-label="Hide sales"
              onClick={(e) => { e.stopPropagation(); toggleHideSales(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggleHideSales(); } }}
            >
              <EyeOffIcon />
            </span>
            <span className={`${styles.funnelNum} ${styles.numPurchased}`}>{A.purchased}</span>
            <span className={styles.funnelLabel}>Purchased</span>
            <span className={styles.funnelPct}>
              {funnelPct(A.purchased, base)} {baseLabel} · {f.albumBuyers} albums{f.revenue != null && ` · $${f.revenue}`}
            </span>
            {parentsLine(f.purchasers)}
            <span className={styles.funnelSplit}>{stageSplit('purchasers')}</span>
            {/* No "stuck here" — Purchased is the end of the funnel, a good place to be. */}
          </button>
          )}
        </div>
        );
      })()}

      {funnel && audienceStages.size > 0 && (
        <AudienceBuilder
          key={[...audienceStages].sort().join('+')}
          eventId={eventId}
          eventName={summary?.event?.name ?? ''}
          eventSlug={funnel?.funnel ? funnel.event?.slug ?? null : null}
          stages={[...audienceStages].sort()}
          onClose={() => setAudienceStages(new Set())}
          onSent={() => {
            setTimelineRefreshKey((k) => k + 1);
            setTimelineOpen(true);
            refreshAll({ silent: true });
          }}
          onSelectAthlete={handleSelectAthleteFromAudience}
        />
      )}

      <CampaignTimeline
        eventId={eventId}
        eventName={summary?.event?.name ?? ''}
        eventSlug={funnel?.event?.slug ?? null}
        refreshKey={timelineRefreshKey}
        open={timelineOpen}
        onToggle={() => setTimelineOpen((o) => !o)}
        onLoaded={setTimeline}
      />

      <div className={styles.campaign}>
        <button
          className={styles.campaignHeader}
          onClick={() => setUploadOpen((o) => !o)}
        >
          Upload photos (browser → FTP)
          {uploadQueue.running && <span className={styles.uploadLive}> · uploading, {uploadQueue.pending.length} to go</span>}
          <span className={styles.campaignChevron}>{uploadOpen ? '▾' : '▸'}</span>
        </button>
        {uploadOpen && (
          <div className={styles.campaignBody}>
            <PhotoUploader eventId={eventId} timezone={summary?.event?.timezone} />
          </div>
        )}
      </div>

      {(() => {
        const active = activeCards(filter, { asParent });
        // Each sign renders inside the unit of the card after it, so a wrap
        // moves them together and a sign never dangles at a row end.
        const statusUnit = ([key, label, numCls, n, title], sign) => (
          <span key={key} className={styles.countUnit}>
            {sign && <span className={styles.countOp}>{sign}</span>}
            <button
              className={`${styles.countCard} ${active.statuses.includes(key) ? styles.countActive : ''}`}
              onClick={() => applyFilter(toggleStatus(filter, key))}
              title={`${title} — click several to combine`}
            >
              <span className={`${styles.countNum} ${numCls}`}>{n}</span> {label}
            </button>
          </span>
        );
        const [matched, manual, review, suppressed] = [
          ['matched', 'Rekognized', styles.numMatched, summary.counts.matched, 'Recognized by AWS Rekognition at or above the threshold (manual/inferred are under Manual)'],
          ['assigned', 'Manual', styles.numManual, summary.counts.manualMatched ?? 0, 'Manual or inferred matches'],
          ['review', 'Review', styles.numReview, summary.counts.review ?? 0, 'Held for a human — low score, adult face, small face, or eyes closed'],
          ['suppressed', 'Suppressed', styles.numSuppressed, summary.counts.suppressed ?? 0, 'Matches an operator hid from the parent — restorable'],
        ];
        const op = (sign) => (attendeeId ? sign : null);
        return (
          <div className={styles.counts}>
            {/* Equation group: Photos = Rekognized + Manual + Review + Suppressed
                (signs and the bracket only with an athlete selected). */}
            <div className={styles.countEquation}>
              <span className={styles.countUnit}>
                <button
                  className={`${styles.countCard} ${active.photos ? styles.countActive : ''}`}
                  onClick={() => applyFilter(clearStatuses(filter))}
                  title={attendeeId
                    ? 'Every photo with any match for this athlete — the four cards to the right add up to it'
                    : 'Every live photo in the event — click a status to narrow; click several to combine'}
                >
                  <span className={styles.countNum}>{summary.counts.photos}</span> Photos
                </button>
              </span>
              {attendeeId ? (
                <span className={`${styles.countUnit} ${styles.parentSeesUnit}`}>
                  <span className={styles.countOp}>=</span>
                  <span className={styles.parentSeesCards}>
                    {statusUnit(matched, null)}
                    {statusUnit(manual, '+')}
                  </span>
                  <span className={styles.parentSeesBracket} />
                  <span
                    className={styles.parentSeesCaption}
                    title="This athlete's matched + manual photos are exactly what shows in the parent's app. Other athletes under the same parent add their own photos."
                  >
                    What the parent sees in the app
                  </span>
                </span>
              ) : (
                <>
                  {statusUnit(matched, null)}
                  {statusUnit(manual, null)}
                </>
              )}
              {statusUnit(review, op('+'))}
              {statusUnit(suppressed, op('+'))}
            </div>
            {/* No Unmatched card (SCRUM-408, Imad 2026-09-19): it has no meaning
                per athlete and event-wide it was noise. Photos nobody is matched
                to are the red-badge tiles in All photos; the `unmatched` view
                itself still exists in the API. */}
            {/* Side group: the sales tags and Deleted, one unit that drops below
                the equation when the column is too narrow for both. Purchased
                (like In carts) stays visible under "Hide sales" — a photo status
                with no dollar amounts. */}
            <div className={styles.countSide}>
              {[
                ['incart', 'In carts', styles.numInCart, summary.counts.inCarts ?? 0],
                ['purchased', 'Purchased', styles.numPurchased, summary.counts.purchased ?? 0],
              ].map(([key, label, numCls, n]) => (
                <button
                  key={key}
                  className={`${styles.countCard} ${active.tags.includes(key) ? styles.countActive : ''}`}
                  onClick={() => applyFilter(toggleTag(filter, key))}
                  title="Narrows whatever is selected on the left"
                >
                  <span className={`${styles.countNum} ${numCls}`}>{n}</span> {label}
                </button>
              ))}
              <button
                className={`${styles.countCard} ${active.deleted ? styles.countActive : ''}`}
                onClick={() => applyFilter(DELETED, null)}
              >
                <span className={styles.countNum}>{summary.counts.deleted}</span> Deleted
              </button>
            </div>
          </div>
        );
      })()}

      <h3 className={styles.rosterTitle}>
        Athletes ({visibleRoster.length}{visibleRoster.length !== summary.roster.length ? ` of ${summary.roster.length}` : ''})
        <span className={styles.rosterSortToggle}>
          <button
            className={rosterSort === 'coverage' ? styles.rosterSortActive : ''}
            onClick={() => setRosterSort('coverage')}
          >
            Least covered first
          </button>
          <button
            className={rosterSort === 'alpha' ? styles.rosterSortActive : ''}
            onClick={() => setRosterSort('alpha')}
          >
            A–Z
          </button>
          <input
            type="search"
            className={styles.rosterSearch}
            placeholder="Search athlete or parent…"
            value={rosterQuery}
            onChange={(e) => setRosterQuery(e.target.value)}
            aria-label="Search athletes by athlete or parent name"
          />
        </span>
      </h3>
      <div className={styles.roster}>
        {visibleRoster.map((r) => (
          <button
            key={r.attendeeId}
            className={`${styles.athleteCard} ${attendeeId === r.attendeeId ? styles.athleteActive : ''}`}
            ref={(el) => { rosterCardRefs.current[r.attendeeId] = el; }}
            onClick={() => applyFilter(keepOnAthleteChange(filter), attendeeId === r.attendeeId ? null : r.attendeeId)}
          >
            {r.selfieUrl ? (
              <img src={s3Url(r.selfieUrl)} alt={r.fullName} className={styles.athleteAvatar} />
            ) : (
              <div className={styles.athleteAvatarEmpty}>?</div>
            )}
            <span className={styles.athleteName}>{r.fullName}</span>
            {r.rsvp === false && r.matchCount === 0 ? (
              <span className={styles.athleteNoRsvp} title="matched on this event's photos but not joined">
                no RSVP
              </span>
            ) : (
              <span className={`${styles.athleteCount} ${r.matchCount === 0 ? styles.athleteZero : ''}`}>
                {r.matchCount}
              </span>
            )}
            {r.parentPurchase && (
              <span className={styles.athletePaid}>$</span>
            )}
            {r.parentInCart && (
              <span className={styles.athleteCart}>🛒</span>
            )}
            {r.reviewCount > 0 && (
              <span className={styles.athleteReview} title="matches to review">{r.reviewCount}?</span>
            )}
          </button>
        ))}
      </div>

      <div className={styles.toolbar} ref={gridAnchorRef}>
        <span className={styles.viewLabel}>
          {filterTitle(filter, !!selectedAthlete, asParent)}
          {selectedAthlete && ` — ${selectedAthlete.fullName.trim()}`}
          {' '}<span className={styles.subtle}>({total})</span>
          {selectedAthlete && (
            <span className={styles.parentInfo}>
              Parent: {selectedAthlete.parentName || '—'}
              {selectedAthlete.parentEmail ? ` · ${selectedAthlete.parentEmail}` : ''}
            </span>
          )}
        </span>
        {selectedAthlete && (
          <button className={styles.profileBtn} onClick={() => setProfileOpen(true)}>
            View profile
          </button>
        )}
        {selectedAthlete && (
          <button className={styles.rematchBtn} onClick={handleRematch} disabled={rematching}>
            {rematching ? 'Matching…' : 'Re-run matching'}
          </button>
        )}
        {selectedAthlete && total > 0 && (
          <button className={styles.zipBtn} onClick={handleDownloadZip} disabled={downloading}>
            {downloading ? 'Preparing…' : `Download all ${total} (zip)`}
          </button>
        )}
        <div className={styles.toolbarRight}>
          {(photosPage?.cameras?.length ?? 0) > 1 && (
            <select
              className={styles.sortBtn}
              value={camera}
              onChange={(e) => { setCamera(e.target.value); setPage(1); }}
              title="Camera body (from EXIF)"
            >
              <option value="">All cameras</option>
              {photosPage.cameras.map((c) => (
                <option key={c.model ?? 'none'} value={c.model ?? ''} disabled={!c.model}>
                  {c.model ?? 'unknown'} · {c.photos.toLocaleString()}
                </option>
              ))}
            </select>
          )}
          {attendeeId && (
            <button
              className={`${styles.sortBtn} ${asParent ? styles.sortBtnOn : ''}`}
              onClick={() => { setParentView((v) => !v); setPage(1); setViewerPhotoId(null); }}
              title={asParent
                ? 'Showing what this family sees in the app: every athlete under the same parent, in the app\'s order. #n is the number in the viewer (P#n on the Purchased tab).'
                : 'Show the gallery exactly as the parent sees it in the app — same photos, same order, numbered'}
            >
              {asParent ? 'As parent sees it ✓' : 'As parent sees it'}
            </button>
          )}
          {!asParent && (
            <>
              <button
                className={styles.sortBtn}
                onClick={() => { setOrder(order === 'asc' ? 'desc' : 'asc'); setPage(1); }}
              >
                {order === 'asc' ? 'Oldest first ↑' : 'Newest first ↓'}
              </button>
            </>
          )}
          {lastPage > 1 && (
            <span className={styles.pager}>
              <button disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
              {page} / {lastPage}
              <button disabled={page >= lastPage} onClick={() => setPage(page + 1)}>›</button>
            </span>
          )}
        </div>
      </div>

      {status && <div className={styles.status}>{status}</div>}

      <div className={`${styles.grid} ${photosLoading ? styles.gridLoading : ''}`}>
        {photos.map((p, i) => (
          <React.Fragment key={p.id}>
          {p.run && (i === 0 || photos[i - 1].run?.index !== p.run.index) && (
            <div className={styles.runDivider} title="One camera's burst with no pause over 90 s — usually one routine">
              📷 {p.run.cameraModel ?? 'unknown camera'} · {fmtClock(p.run.start)}–{fmtClock(p.run.end)} · {p.run.frames} frame{p.run.frames === 1 ? '' : 's'}
            </div>
          )}
          {/* With an athlete selected the outline is her bucket on this photo
              (the counter colours: blue auto, purple manual, light-blue near
              miss, red suppressed). No athlete → no outlines: several kids'
              outlines on one tile would only confuse; the pills carry the
              rest and look the same either way. */}
          {(() => {
          const tileMatch = attendeeId ? p.matchedAttendees.find((m) => m.attendeeId === attendeeId) : null;
          const actions = tileActions(tileMatch, { attendeeId, asParent, deleted: filter.deleted, showSuppressed });
          return (
          <div className={styles.tileWrap}>
          <button
            className={`${styles.tile} ${attendeeId ? (TILE_BUCKET_CLASS[matchBucket(tileMatch)] ?? '') : ''}`}
            onClick={() => setViewerPhotoId(p.id)}
          >
            <img
              src={s3Url(p.thumbnailUrl || p.originalUrl)}
              alt=""
              loading="lazy"
              className={styles.tileImg}
            />
            <span
              className={`${styles.tileBadge} ${p.matchedAttendees.length === 0 ? styles.tileBadgeNone : ''}`}
            >
              {p.matchedAttendees.length}
            </span>
            {(p.purchases > 0 || p.albumCovered) && (
              <span className={styles.tileSoldBadge}>$ {p.purchases > 1 ? p.purchases : ''}</span>
            )}
            {p.inCart && <span className={styles.tileCartBadge}>🛒</span>}
            {p.pos != null && (
              <span
                className={`${styles.tilePosBadge} ${p.bought ? styles.tilePosBought : ''}`}
                title={p.bought ? 'Number on the Purchased tab in the app' : 'Number on the Available tab in the app'}
              >
                {p.bought ? `P#${p.pos}` : `#${p.pos}`}
              </span>
            )}
          </button>
          {/* SCRUM-436: the viewer chip's actions, on the tile, for the
              filtered athlete's match only — same rules as MatchChip. */}
          {actions.length > 0 && (
            <span className={styles.tileActions}>
              {actions.map((a) => (
                <button
                  key={a.type}
                  type="button"
                  disabled={tileBusyId === p.id}
                  className={`${styles.faceAction} ${a.type === 'suppress' ? styles.faceActionDanger : ''}`}
                  aria-label={a.type === 'confirm' ? 'Confirm' : a.type === 'restore' ? 'Restore' : a.label}
                  title={a.type === 'confirm' ? `Confirm ${tileMatch.fullName?.trim()} on this photo`
                    : a.type === 'restore' ? `Restore ${tileMatch.fullName?.trim()} on this photo`
                    : a.label === 'Remove' ? `Remove this manual tag of ${tileMatch.fullName?.trim()}`
                    : `Suppress ${tileMatch.fullName?.trim()} — hidden from the parent and never re-matched`}
                  onClick={() => handleTileAction(p, tileMatch, a)}
                >
                  {a.type === 'confirm' ? '✓' : a.type === 'restore' ? '↺ Restore' : a.label === 'Remove' ? '✕ Remove' : '✕'}
                </button>
              ))}
            </span>
          )}
          </div>
          );
          })()}
          </React.Fragment>
        ))}
        {photos.length === 0 && (
          <div className={styles.empty}>No photos in this view</div>
        )}
      </div>
      {lastPage > 1 && (
        <div className={styles.pagerBottom}>
          <button disabled={page <= 1} onClick={() => { setPage(page - 1); gridAnchorRef.current?.scrollIntoView({ block: 'start' }); }}>‹ Prev</button>
          <span>{page} / {lastPage}</span>
          <button disabled={page >= lastPage} onClick={() => { setPage(page + 1); gridAnchorRef.current?.scrollIntoView({ block: 'start' }); }}>Next ›</button>
        </div>
      )}

      {viewerPhoto && (
        <div className={styles.viewerOverlay} onClick={() => setViewerPhotoId(null)}>
          <div className={styles.viewer} onClick={(e) => e.stopPropagation()}>
            <div className={styles.viewerMeta}>
              <span>ID {viewerPhoto.id.substring(0, 8)}{viewerPhoto.originalFilename ? ` · ${viewerPhoto.originalFilename}` : ''}</span>
              <span title="Uploaded">↑ {new Date(viewerPhoto.createdAt).toLocaleString()}</span>
              {viewerPhoto.takenAt && (
                <span title="Camera capture time (camera clock, not corrected)">
                  📷 {viewerPhoto.cameraModel ?? 'camera'} · {viewerPhoto.takenAt.replace('T', ' ').substring(0, 19)}
                </span>
              )}
              <span>{held ? `no longer in this view · ${photos.length} on this page` : `${viewerIndex + 1} / ${photos.length} on this page`}</span>
              {viewerPhoto.pos != null && (
                <span title="The number the parent sees in the app viewer">
                  · app {viewerPhoto.bought ? `Purchased #${viewerPhoto.pos}` : `#${viewerPhoto.pos}`}
                </span>
              )}
              <span className={styles.viewerToggle}>
                <button type="button" className={viewerMode === 'clean' ? styles.toggleOn : ''} onClick={() => setViewerMode('clean')} title="Clean 1200px preview with every face boxed">Clean</button>
                <button type="button" className={viewerMode === 'parent' ? styles.toggleOn : ''} onClick={() => setViewerMode('parent')} title="Exactly what the parent sees in the app">Parent view</button>
              </span>
            </div>
            {/* Three columns (SCRUM-408, Imad): left rail = who is on this
                photo, centre = the photo at full height, right rail = everyone
                else to tag from. Stacks back into one column under 1100px. */}
            <div className={styles.viewerBody}>
            <div className={`${styles.rail} ${styles.railLeft}`}>
              <div className={styles.sectionLabel}>
                On this photo ({viewerMatches.length})
                {viewerMatches.length === 0 && (
                  <span className={styles.subtle}> — nobody yet</span>
                )}
              </div>
              <div className={styles.railList}>
                {viewerMatches.map((att) => {
                  const rosterEntry = summary.roster.find((r) => r.attendeeId === att.attendeeId);
                  return (
                    <React.Fragment key={att.attendeeId}>
                    <MatchChip
                      match={att}
                      threshold={summary.event.matchThreshold}
                      selfieUrl={rosterEntry?.selfieUrl ? s3Url(rosterEntry.selfieUrl) : ''}
                      onConfirm={(m) => handleMatchAction(viewerPhoto, m, confirmMatch, 'Confirmed')}
                      onSuppress={(m) => handleMatchAction(viewerPhoto, m, suppressMatch, m.manual ? 'Removed' : 'Suppressed')}
                      onRestore={(m) => handleMatchAction(viewerPhoto, m, restoreMatch, 'Restored')}
                      readOnly={filter.deleted}
                      onShowProfile={rosterEntry ? () => togglePeek(rosterEntry.attendeeId) : undefined}
                    />
                    {peekAttendeeId === att.attendeeId && peekStrip}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
            <div className={styles.viewerImageRow}>
              <button
                className={styles.navBtn}
                disabled={!prevPhoto}
                onClick={() => setViewerPhotoId(prevPhoto.id)}
              >‹</button>
              {(() => {
                const clean = viewerMode === 'clean' && viewerPhoto.ownedUrl;
                // SCRUM-417: the 700px watermarked preview no longer renders at
                // upload — only at the first visible match. In Parent view on a
                // not-yet-matched photo, mediumUrl is null: fall back to the
                // clean 1200px (or original) and say so, since no real parent
                // sees this photo yet either.
                const parentUnmatched = viewerMode === 'parent' && !viewerPhoto.mediumUrl;
                const src = clean
                  ? s3Url(viewerPhoto.ownedUrl)
                  : parentUnmatched
                    ? s3Url(viewerPhoto.ownedUrl || viewerPhoto.originalUrl)
                    : s3Url(viewerPhoto.mediumUrl || viewerPhoto.originalUrl);
                const rects = clean
                  ? faceRects(viewerPhoto.faces, viewerPhoto.width, viewerPhoto.height, true)
                  : [];
                return (
                  <div className={styles.viewerImgWrap}>
                    <img src={src} alt="" className={styles.viewerImg} />
                    {rects.map((r) => {
                      // A named face is not automatically a live match: green +
                      // name only when the parent actually sees it, red dashed
                      // for an operator's suppression, grey for a face in review
                      // (name in the tooltip so it does not read as a match).
                      // A review face is drawn too — light-blue dashed, name with a
                      // "?" — so the operator can see which face `c` will confirm.
                      const suppressed = r.status === 'suppressed';
                      const review = !suppressed && !r.visible && !!r.attendeeId;
                      const match = viewerPhoto.matchedAttendees.find((m) => m.attendeeId === r.attendeeId);
                      const bucket = matchBucket(match);
                      // SCRUM-357 (fix wave): reasons for the match this face is tied
                      // to (derives "low score" too), but ONLY while that match is
                      // actually held (auto + not visible) — the same guard
                      // MatchChip.jsx applies. Without it a confirmed/suppressed row's
                      // untouched-but-stale reviewReasons rendered as if still in
                      // Review. A named face with no live match row falls back to the
                      // face's own stored reasons from faceOverlay. `?? k` covers a
                      // reason key the admin doesn't recognise yet (API-ahead-of-admin).
                      const reasons = faceReasons(match, r.reviewReasons, summary.event.matchThreshold)
                        .map((k) => REASON_LABEL[k] ?? k);
                      const cls = suppressed
                        ? styles.faceBoxSuppressed
                        : review
                          ? styles.faceBoxReview
                          : bucket === 'manual'
                            ? styles.faceBoxManual
                            : r.visible
                              ? styles.faceBoxMatched
                              : '';
                      const label = suppressed ? 'suppressed' : review ? `${r.fullName}?` : r.visible ? r.fullName : null;
                      // A face with no live match is clickable either way round:
                      // athlete already picked below → this click is the tag;
                      // no athlete yet → the face waits for the next athlete card.
                      const pickable = !r.visible && !viewerPhoto.deletedAt;
                      const pending = pendingFaceId === r.faceId;
                      return (
                        <div
                          key={r.faceId}
                          className={`${styles.faceBox} ${cls} ${pickable ? styles.faceBoxPickable : ''} ${pending ? styles.faceBoxPending : ''}`}
                          style={{ left: `${r.left}%`, top: `${r.top}%`, width: `${r.width}%`, height: `${r.height}%` }}
                          role={pickable ? 'button' : undefined}
                          onClick={
                            !pickable
                              ? undefined
                              : assignTarget
                                ? () => handleAssign(viewerPhoto, r.faceId)
                                : () => setPendingFaceId((cur) => (cur === r.faceId ? null : r.faceId))
                          }
                          title={
                            pickable && assignTarget
                              ? `Assign ${summary.roster.find((x) => x.attendeeId === assignTarget)?.fullName || 'athlete'} to this face · ${r.facePx}px`
                              : pickable
                                ? `${pending ? 'Selected — now tap an athlete on the right' : 'Click, then tap an athlete on the right'} · ${r.facePx}px`
                                : r.fullName
                                ? `${r.fullName} · ${suppressed ? 'suppressed' : r.visible ? 'match' : 'review'}${reasons.length ? ` · ${reasons.join(', ')}` : ''} · face ${r.facePx}px`
                                : `unmatched face · ${r.facePx}px`
                          }
                        >
                          {label && <span className={styles.faceLabel}>{label}</span>}
                        </div>
                      );
                    })}
                    {parentUnmatched && (
                      <div className={`${styles.subtle} ${styles.viewerCaption}`}>
                        Not matched to anyone — no parent sees this photo
                      </div>
                    )}
                  </div>
                );
              })()}
              <button
                className={styles.navBtn}
                disabled={!nextPhoto}
                onClick={() => setViewerPhotoId(nextPhoto.id)}
              >›</button>
            </div>
            {!filter.deleted ? (
              <div className={`${styles.rail} ${styles.railRight}`}>
                <div className={styles.sectionLabel} title="Tap an athlete, then a face box in the photo (or the other way round); Assign tags without a face">Add a match</div>
                <input
                  className={styles.railSearch}
                  placeholder="filter names…"
                  value={railQuery}
                  onChange={(e) => setRailQuery(e.target.value)}
                />
                <div className={styles.railList}>
                {summary.roster
                  .filter((r) => !viewerPhoto.matchedAttendees.some((a) => a.attendeeId === r.attendeeId))
                  .filter((r) => !railQuery.trim() || r.fullName.toLowerCase().includes(railQuery.trim().toLowerCase()))
                  .slice()
                  .sort((a, b) => a.fullName.trim().localeCompare(b.fullName.trim()))
                  .map((r) => (
                    <React.Fragment key={r.attendeeId}>
                    <div
                      role="button"
                      tabIndex={0}
                      className={`${styles.faceCard} ${assignTarget === r.attendeeId ? styles.faceActive : ''}`}
                      onClick={() =>
                        pendingFaceId ? handleAssign(viewerPhoto, pendingFaceId, r.attendeeId) : setAssignTarget(assignTarget === r.attendeeId ? '' : r.attendeeId)
                      }
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (pendingFaceId) handleAssign(viewerPhoto, pendingFaceId, r.attendeeId); else setAssignTarget(assignTarget === r.attendeeId ? '' : r.attendeeId); } }}
                    >
                      {r.selfieUrl ? (
                        <img src={s3Url(r.selfieUrl)} alt={r.fullName} className={styles.faceImg} />
                      ) : (
                        <div className={styles.faceImgEmpty}>?</div>
                      )}
                      <span className={styles.faceName}>{r.fullName}</span>
                      <span className={styles.faceSubCount}>
                        {r.matchCount} photo{r.matchCount === 1 ? '' : 's'}
                      </span>
                      <button
                        type="button"
                        className={styles.facePeek}
                        title="Open this athlete's reference selfies to compare"
                        aria-label={`Show ${r.fullName} profile`}
                        onClick={(e) => { e.stopPropagation(); togglePeek(r.attendeeId); }}
                      >
                        🔍
                      </button>
                    </div>
                    {peekAttendeeId === r.attendeeId && peekStrip}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ) : (
              <div className={`${styles.rail} ${styles.railRight}`} />
            )}
            </div>
            <div className={styles.viewerFooter}>
            <div className={styles.viewerActions}>
              {filter.deleted ? (
                <>
                  <button className={styles.assignBtn} onClick={() => handleRestore(viewerPhoto)}>
                    Restore photo
                  </button>
                  <button className={styles.closeBtn} onClick={() => setViewerPhotoId(null)}>Close</button>
                </>
              ) : (
                <>
                  <button
                    className={styles.assignBtn}
                    disabled={!assignTarget}
                    onClick={() => handleAssign(viewerPhoto)}
                  >
                    {assignTarget
                      ? `Assign to ${summary.roster.find((r) => r.attendeeId === assignTarget)?.fullName || 'athlete'}`
                      : 'Assign: tap an athlete on the right (or click a face box in the photo)'}
                  </button>
                  <button className={styles.deleteBtn} onClick={() => setConfirmDelete(viewerPhoto)}>
                    Delete photo
                  </button>
                  <button className={styles.closeBtn} onClick={() => setViewerPhotoId(null)}>Close</button>
                </>
              )}
            </div>
            {!filter.deleted && (
              <div className={styles.keyLegend} title="Keyboard shortcuts">
                <kbd>x</kbd> suppress · <kbd>c</kbd> confirm · <kbd>d</kbd> delete · <kbd>←</kbd> <kbd>→</kbd> · <kbd>esc</kbd>
                <span className={styles.keyTarget}>
                  {viewerTarget
                    ? ` — x/c act on ${viewerTarget.fullName?.trim()}`
                    : viewerPhoto.matchedAttendees.length > 1
                      ? ' — select an athlete in the roster for x/c'
                      : viewerPhoto.matchedAttendees.length === 0
                        ? ' — no match on this photo for x/c'
                        : ''}
                </span>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {profileOpen && selectedAthlete && (
        <div className={styles.viewerOverlay} onClick={closeProfile}>
          <div className={styles.viewer} onClick={(e) => e.stopPropagation()}>
            <div className={styles.profileHeader}>
              <h3 className={styles.profileName}>{selectedAthlete.fullName}</h3>
              <span className={styles.subtle}>
                {selectedAthlete.matchCount} matched photo{selectedAthlete.matchCount === 1 ? '' : 's'}
                {selectedAthlete.parentName && (
                  <>
                    {' · Parent: '}{selectedAthlete.parentName}
                    {selectedAthlete.parentId && (
                      <ReachBadge
                        pushValid={selectedAthlete.pushValid}
                        oneSignalID={selectedAthlete.oneSignalID}
                        hasPhone={selectedAthlete.parentHasPhone}
                        pushCheckedAt={selectedAthlete.pushCheckedAt}
                      />
                    )}
                  </>
                )}
                {selectedAthlete.parentEmail ? ` · ${selectedAthlete.parentEmail}` : ''}
              </span>
            </div>
            <div className={styles.profileShots}>
              {(selectedAthlete.selfieUrls?.length ? selectedAthlete.selfieUrls : [selectedAthlete.selfieUrl])
                .filter(Boolean)
                .map((u, i) => (
                  <a key={i} href={s3Url(u)} target="_blank" rel="noreferrer" title="Open full resolution">
                    <img src={s3Url(u)} alt={`${selectedAthlete.fullName} reference ${i + 1}`} className={styles.profileShot} />
                  </a>
                ))}
              {!(selectedAthlete.selfieUrls?.length || selectedAthlete.selfieUrl) && (
                <span className={styles.subtle}>No reference photos on file</span>
              )}
            </div>
            <div className={styles.viewerActions}>
              <span className={styles.subtle}>These are the shots face recognition matches against — click one for full resolution</span>
              <button className={styles.closeBtn} onClick={closeProfile}>Close</button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          message="Delete this photo? It disappears from every attendee's gallery immediately. Individually purchased photos are protected and will refuse."
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </Page>
  );
}
